/**
 * File: zohoImport.ts
 * Path: packages/config/src/accounting/zohoImport.ts
 * Description: Bringing Zoho Books' history in as real journals.
 *
 * Liam exports Invoices, Customer Payments and Expenses from Zoho Books as
 * CSV. Each record becomes one journal, the way Zoho itself booked it:
 *
 * - an invoice:  Dr Trade debtors, Cr Sales (one credit per invoice line)
 * - a payment:   Dr Bank, Cr Trade debtors
 * - an expense:  Dr the expense account, Cr Bank (a refund the other way)
 * - a shareholder loan line (Account Transactions of "<name> - Shareholder
 *   Loan"): money in is Dr Bank, Cr that shareholder's loan account; money
 *   paid back is the other way. One loan account per shareholder.
 *
 * Every journal carries a stable reference built from Zoho's own ids
 * ("zoho:invoice:INV-000010"), so the import can be run again after a fresh
 * export and only what is new goes in. Nothing here writes anything: the
 * result is a plan the admin shows as a dry run before Liam posts it.
 *
 * Who each record belongs to: invoices and payments name the customer.
 * Expenses almost never do in Zoho, so the description is searched for a
 * known client's name ("Pondo Tours maintenance") as a suggestion, marked
 * as a guess for Liam to confirm.
 */

import type { AccountType, LedgerAccount } from "./accountingTypes";
import type { TemplateAccount } from "./chartTemplates";
import { parseCsv } from "./csv";
import { financialYearEnd, JOURNAL_LIMITS } from "./ledger";
import { formatCents } from "./money";

/* ---------------------------------------------------------------------------
 * Reading the files
 * ------------------------------------------------------------------------ */

export type ZohoFileKind = "invoices" | "payments" | "expenses" | "contacts" | "loans";

export const ZOHO_FILE_LABELS: Record<ZohoFileKind, string> = {
  invoices: "Invoices",
  payments: "Customer payments",
  expenses: "Expenses",
  contacts: "Contacts",
  loans: "Shareholder loan",
};

export interface ZohoInvoiceLine {
  item: string;
  description: string;
  amountCents: number;
}

export interface ZohoInvoice {
  number: string;
  date: string;
  /** Zoho's due date, when the export has one. */
  dueOn?: string;
  customer: string;
  project: string;
  status: string;
  totalCents: number;
  lines: ZohoInvoiceLine[];
}

export interface ZohoPayment {
  /** Zoho's own id for the payment, which never changes. */
  id: string;
  number: string;
  date: string;
  customer: string;
  amountCents: number;
  reference: string;
  /** The invoices it paid and how much of each. */
  invoices: { number: string; amountCents: number }[];
  /** Set by the import when the bank proved Zoho's date wrong and it was changed. */
  fixed?: boolean;
}

export interface ZohoExpense {
  /** Zoho's own id for the expense, which never changes. */
  id: string;
  entry: string;
  date: string;
  account: string;
  description: string;
  vendor: string;
  customer: string;
  project: string;
  paidThrough: string;
  /** Below zero for a refund. */
  amountCents: number;
}

/** One line of a shareholder loan account, as Zoho recorded it. */
export interface ZohoLoanLine {
  /** Zoho's own transaction id, which never changes. */
  id: string;
  date: string;
  /** "Liam Daries": the account name without " - Shareholder Loan". */
  shareholder: string;
  /** Money the company paid back to the shareholder. */
  debitCents: number;
  /** Money the shareholder put in. */
  creditCents: number;
}

export interface ZohoBundle {
  invoices: ZohoInvoice[];
  payments: ZohoPayment[];
  expenses: ZohoExpense[];
  loans: ZohoLoanLine[];
  /** Customer names from the Contacts export, looked for in expense descriptions. */
  contacts: string[];
  /** Which file each kind came from, for the screen. */
  files: { name: string; kind: ZohoFileKind | null; rows: number }[];
  /** Problems with the files themselves. */
  issues: string[];
}

const norm = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Which Zoho export a file is, from its header row. Null: not one we read. */
export const detectZohoFile = (header: readonly string[]): ZohoFileKind | null => {
  const cells = new Set(header.map(norm));
  if (cells.has("invoice number") && cells.has("item total")) return "invoices";
  if (cells.has("customerpayment id") && cells.has("amount applied to invoice")) return "payments";
  if (cells.has("expense date") && cells.has("expense account")) return "expenses";
  if (cells.has("display name") && cells.has("customer sub type")) return "contacts";
  if (cells.has("account name") && cells.has("transaction type") && cells.has("offset account type")) return "loans";
  return null;
};

/**
 * Zoho writes amounts with three decimals ("462.700"). Rounded to the cent;
 * null when the cell isn't a plain number.
 */
export const zohoCents = (cell: string): number | null => {
  const text = cell.trim().replace(/,/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  const cents = Math.round(Number(text) * 100);
  return Number.isSafeInteger(cents) ? cents : null;
};

const isDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

type Row = (name: string) => string;

const rowsOf = (table: string[][]): Row[] => {
  const header = table[0].map(norm);
  return table.slice(1).map((cells) => (name: string) => {
    const i = header.indexOf(norm(name));
    return i === -1 ? "" : (cells[i] ?? "").trim();
  });
};

const readInvoices = (rows: Row[], file: string, issues: string[]): ZohoInvoice[] => {
  const byNumber = new Map<string, ZohoInvoice>();
  rows.forEach((r, i) => {
    const number = r("Invoice Number");
    const date = r("Invoice Date");
    const amount = zohoCents(r("Item Total"));
    const total = zohoCents(r("Total"));
    if (!number || !isDate(date) || amount === null || total === null) {
      issues.push(`${file}, row ${i + 2}: couldn't read the invoice number, date or amount.`);
      return;
    }
    const invoice =
      byNumber.get(number) ??
      ({
        number,
        date,
        customer: r("Customer Name"),
        project: r("Project Name"),
        status: r("Invoice Status"),
        ...(isDate(r("Due Date")) ? { dueOn: r("Due Date") } : {}),
        totalCents: total,
        lines: [],
      } satisfies ZohoInvoice);
    invoice.lines.push({ item: r("Item Name"), description: r("Item Desc"), amountCents: amount });
    byNumber.set(number, invoice);
  });
  return [...byNumber.values()];
};

const readPayments = (rows: Row[], file: string, issues: string[]): ZohoPayment[] => {
  const byId = new Map<string, ZohoPayment>();
  rows.forEach((r, i) => {
    const id = r("CustomerPayment ID");
    const date = r("Date");
    const amount = zohoCents(r("Amount"));
    if (!id || !isDate(date) || amount === null) {
      issues.push(`${file}, row ${i + 2}: couldn't read the payment's id, date or amount.`);
      return;
    }
    const payment =
      byId.get(id) ??
      ({
        id,
        number: r("Payment Number"),
        date,
        customer: r("Customer Name"),
        amountCents: amount,
        reference: r("Reference Number"),
        invoices: [],
      } satisfies ZohoPayment);
    const applied = zohoCents(r("Amount Applied to Invoice"));
    if (r("Invoice Number") && applied !== null) {
      payment.invoices.push({ number: r("Invoice Number"), amountCents: applied });
    }
    byId.set(id, payment);
  });
  return [...byId.values()];
};

const readExpenses = (rows: Row[], file: string, issues: string[]): ZohoExpense[] =>
  rows.flatMap((r, i) => {
    const id = r("Expense Reference ID");
    const date = r("Expense Date");
    const amount = zohoCents(r("Total"));
    if (!id || !isDate(date) || amount === null) {
      issues.push(`${file}, row ${i + 2}: couldn't read the expense's id, date or amount.`);
      return [];
    }
    return [
      {
        id,
        entry: r("Entry Number"),
        date,
        account: r("Expense Account"),
        description: r("Expense Description").replace(/\s+/g, " "),
        vendor: r("Vendor"),
        customer: r("Customer Name"),
        project: r("Project Name"),
        paidThrough: r("Paid Through"),
        amountCents: amount,
      },
    ];
  });

const LOAN_SUFFIX = / - shareholder loan$/i;

const readLoans = (rows: Row[], file: string, issues: string[]): ZohoLoanLine[] =>
  rows.flatMap((r, i) => {
    const account = r("Account Name");
    const id = r("Transaction Id");
    const date = r("Date");
    const debit = r("Debit") === "" ? 0 : zohoCents(r("Debit"));
    const credit = r("Credit") === "" ? 0 : zohoCents(r("Credit"));
    if (!LOAN_SUFFIX.test(account)) {
      issues.push(`${file}, row ${i + 2}: "${account}" isn't a shareholder loan account, so the line was left out.`);
      return [];
    }
    if (!id || !isDate(date) || debit === null || credit === null || (debit > 0) === (credit > 0)) {
      issues.push(`${file}, row ${i + 2}: couldn't read the loan line's id, date or amount.`);
      return [];
    }
    return [{ id, date, shareholder: account.replace(LOAN_SUFFIX, "").trim(), debitCents: debit, creditCents: credit }];
  });

/** Reads any mix of Zoho exports. A file of the same kind twice is read once, except loan files (one per shareholder). */
export const readZohoFiles = (files: readonly { name: string; text: string }[]): ZohoBundle => {
  const bundle: ZohoBundle = { invoices: [], payments: [], expenses: [], loans: [], contacts: [], files: [], issues: [] };
  const seen = new Set<ZohoFileKind>();
  for (const file of files) {
    const table = parseCsv(file.text);
    const kind = table.length > 0 ? detectZohoFile(table[0]) : null;
    bundle.files.push({ name: file.name, kind, rows: Math.max(0, table.length - 1) });
    if (!kind) {
      bundle.issues.push(`${file.name} was left out: the import reads Zoho's Invoices, Customer Payments, Expenses, Contacts and shareholder loan exports.`);
      continue;
    }
    if (kind !== "loans" && seen.has(kind)) {
      bundle.issues.push(`${file.name} is a second ${ZOHO_FILE_LABELS[kind]} file, so it was left out.`);
      continue;
    }
    seen.add(kind);
    const rows = rowsOf(table);
    if (kind === "invoices") bundle.invoices = readInvoices(rows, file.name, bundle.issues);
    if (kind === "payments") bundle.payments = readPayments(rows, file.name, bundle.issues);
    if (kind === "expenses") bundle.expenses = readExpenses(rows, file.name, bundle.issues);
    if (kind === "loans") bundle.loans.push(...readLoans(rows, file.name, bundle.issues));
    if (kind === "contacts") bundle.contacts = rows.map((r) => r("Display Name")).filter(Boolean);
  }
  return bundle;
};

/* ---------------------------------------------------------------------------
 * Where each Zoho account lands in the chart
 * ------------------------------------------------------------------------ */

const extra = (code: string, name: string, taxLine: string, type: AccountType = "expense"): TemplateAccount => ({
  id: `a-${code}`,
  code,
  name,
  type,
  taxLine,
  role: null,
  money: false,
});

/**
 * Accounts Zoho used that the starting chart doesn't have. The import adds
 * whichever of these it needs (by code) before posting.
 */
export const ZOHO_EXTRA_ACCOUNTS: readonly TemplateAccount[] = [
  extra("1600", "Incorporation costs", "Other non-current assets", "asset"),
  extra("2310", "Shareholder loan: Liam Daries", "Loans from shareholders / directors", "liability"),
  extra("2320", "Shareholder loan: Eliezer E. Mitchell", "Loans from shareholders / directors", "liability"),
  extra("4800", "Other sales (not web work)", "Sales", "income"),
  extra("5100", "Cost of sales: subcontractors on client work", "Cost of sales"),
  extra("5200", "Cost of sales: goods bought for resale", "Cost of sales"),
  extra("6010", "Contractor fees: Liam", "Other expenses"),
  extra("6240", "Insurance", "Insurance"),
  extra("6250", "Client meetings", "Other expenses"),
  extra("6260", "Equipment and repairs", "Other expenses"),
  extra("6270", "Uniforms and branded clothing", "Other expenses"),
  extra("6280", "Printing and stationery", "Printing and stationery"),
  extra("6350", "Training", "Other expenses"),
  extra("6360", "Donations (s18A)", "Donations"),
  extra("6390", "Sundry expenses", "Other expenses"),
  extra("6410", "Small assets written off", "Other expenses"),
];

/**
 * Zoho expense account (by name) to chart code, as grouped in the books
 * tidy-up (Liam, 2026-10-18; booksTidyData.ts). Anything else goes to
 * Suspense. A few records leave their account's code by who was paid or
 * what was bought: see zohoExpenseCode.
 */
export const ZOHO_EXPENSE_ACCOUNTS: Readonly<Record<string, string>> = {
  "cost of goods sold": "5200",
  "contractor expense": "6010",
  "subcontractor expense": "5100",
  "freelance assistance": "5100",
  "software subscriptions": "6200",
  "it and internet expenses": "6210",
  "telephone expense": "6210",
  "bank fees and charges": "6220",
  "cloud usage expense": "5000",
  "insurance expense": "6240",
  "meeting expense": "6250",
  "equipment expense": "6260",
  "uniform expense": "6270",
  "printing and stationery": "6280",
  research: "6390",
  "statutory fees and returns": "6300",
  "accounting fees": "6300",
  "advertising and marketing": "6310",
  "travel expense": "6320",
  networking: "6310",
  // Zoho keeps this as an asset, not an expense.
  "incorporation cost": "1600",
  "training and development": "6350",
  charity: "6360",
  "license expense": "6200",
  "repairs and maintenance": "6260",
  "miscellaneous expense": "6390",
  "small assets": "6410",
  "interest expense": "6500",
};

/**
 * Zoho accounts whose records are shared between two of the books'
 * accounts: domains bought under "IT and Internet" are cost of sales (5000),
 * and goods bought to sell on under "Miscellaneous" are 5200. Compare with
 * Zoho adds these codes to the account's match, so its totals still tie.
 */
export const ZOHO_EXPENSE_SPLITS: Readonly<Record<string, readonly string[]>> = {
  "it and internet expenses": ["5000"],
  "miscellaneous expense": ["5200"],
};

/** Paid to a domain seller: cost of sales, whatever Zoho account it sat in. */
const DOMAIN_SELLER = /^(godaddy|domains\.co\.za)\b/i;
/** Bought to sell on (toilet seats, glasses). */
const FOR_RESALE = /\bresale\b/i;

/** The chart code a Zoho expense lands on. Null: its Zoho account has no match (it goes to Suspense). */
export const zohoExpenseCode = (exp: Pick<ZohoExpense, "account" | "vendor" | "description">): string | null => {
  const name = norm(exp.account);
  const code = ZOHO_EXPENSE_ACCOUNTS[name];
  if (!code) return null;
  if (name === "it and internet expenses" && DOMAIN_SELLER.test(exp.vendor.trim())) return "5000";
  if (name === "miscellaneous expense" && FOR_RESALE.test(exp.description)) return "5200";
  return code;
};

export const ZOHO_CODES = {
  bank: "1000",
  debtors: "1100",
  suspense: "9999",
  webSales: "4000",
  careSales: "4010",
  hostingSales: "4020",
  otherSales: "4800",
} as const;

/**
 * Which job each invoice was for, from Liam's answers (2026-10-08) and the
 * signed contracts he sent (16 Oct: INV-000008, 000011, 000036). The names
 * are the jobs "Tidy up clients" created under the client in the CRM.
 * Zoho's own project field is mostly empty, so this is what ties the money
 * to a job. A payment follows the invoice it paid.
 */
export const ZOHO_JOB_ASSIGNMENTS: Readonly<Record<string, string>> = {
  "INV-000001": "Sir Bastians website",
  "INV-000015": "Sir Bastians website",
  "INV-000019": "Shuttle Services app",
  "INV-000020": "Shuttle Services app",
  "INV-000026": "Shuttle Services app",
  "INV-000031": "Shuttle Services app",
  "INV-000024": "Shuttle Assist app",
  "INV-000028": "Shuttle Assist app",
  "INV-000029": "Shuttle Assist app",
  "INV-000032": "Shuttle Assist app",
  "INV-000035": "Shuttle Assist app",
  "INV-000027": "Shuttle Assist logo design",
  "INV-000036": "Laundry app",
  "INV-000008": "IC-CPT booking engine",
  "INV-000011": "Agent-Ex CRM",
};

/** "Payment 7 from X for INV-000019, INV-000020": the invoice numbers a payment narration names. */
const invoiceNumbersIn = (text: string): string[] => text.match(/INV-\d+/g) ?? [];

/**
 * The job a journal belongs to, by name: Zoho's project when it has one,
 * else the job Liam assigned to its invoice (or, for a payment, to the first
 * invoice it paid). Null when there is none. Works on journals already
 * posted, which only carry their narration and reference.
 */
/** Invoice numbers Liam confirmed are deleted duplicates, so no note is raised for them. */
export const ZOHO_KNOWN_GAPS: readonly number[] = [33];

export const jobNameFor = (journal: { project: string | null; externalRef: string | null; narration: string }): string | null => {
  if (journal.project) return journal.project;
  const own = journal.externalRef?.startsWith("zoho:invoice:") ? [journal.externalRef.slice("zoho:invoice:".length)] : [];
  for (const number of [...own, ...invoiceNumbersIn(journal.narration)]) {
    const job = ZOHO_JOB_ASSIGNMENTS[number];
    if (job) return job;
  }
  return null;
};

/**
 * Zoho dates that the bank statements prove wrong, by Zoho's own payment id.
 * Applied only while the Zoho date still matches `from`, so a fixed record in
 * Zoho is left alone. 100 Media payment 22 (R5,000) is dated 2024-06-05 in
 * Zoho, a year too early: Zoho's own applied date and the FNB statement both
 * show the money on 5 Jun 2025 (Magtape Credit 100Media). Payment 15 is the
 * separate R5,000 on 27 Aug 2025.
 */
/**
 * Invoices Liam says were paid although Zoho holds no payment and the FNB
 * statements don't show it (2026-10-10). Booked as paid on the invoice date
 * until he gives the real account and date; it is not on the FNB statements,
 * so the FNB year-end tie moves by this amount.
 */
export const ZOHO_ASSUMED_PAID: readonly string[] = [];

/**
 * Invoices Liam says should never have been in the books, by number. The
 * import leaves them out, and reverses the journal when one is already in, so
 * the sale and what the customer "owed" both disappear. INV-000001 (R10,000,
 * Sir Bastians): a duplicate invoice for a project that never happened, with
 * no payment anywhere in Zoho or on the FNB statements (Liam, 2026-10-11).
 */
export const ZOHO_VOIDED_INVOICES: Readonly<Record<string, string>> = {
  "INV-000001": "A duplicate invoice for a project that never happened, so it is taken out of the books.",
};

export const ZOHO_DATE_CORRECTIONS: Readonly<Record<string, { from: string; to: string }>> = {
  "6294320000000203478": { from: "2024-06-05", to: "2025-06-05" },
};

/** Each shareholder's own loan account. Anyone else goes to the general director's loan (2300) and is flagged. */
export const ZOHO_LOAN_ACCOUNTS: Readonly<Record<string, string>> = {
  "liam daries": "2310",
  "eliezer e mitchell": "2320",
};
const GENERAL_LOAN_CODE = "2300";

/**
 * Sales Zoho booked straight to "Sales" with no invoice (the General Ledger
 * shows them; the bank statements give the dates). Liam, 2026-10-08: Jabals
 * kept a deposit on a cancelled project, and Sir-App was a sale to Sir
 * Bastians for the shuttle app. Added only with the loan files, which are
 * the General Ledger half of the import.
 */
export const ZOHO_SALES_RECEIPTS: readonly {
  id: string;
  date: string;
  customer: string;
  project: string;
  amountCents: number;
  what: string;
}[] = [
  { id: "sir-app-2025-01-27", date: "2025-01-27", customer: "Sir Bastians Shuttle Service", project: "Shuttle Services app", amountCents: 130_000, what: "Sir-App Development" },
  { id: "jabals-2025-02-10", date: "2025-02-10", customer: "Jabals Charters", project: "School transport platform", amountCents: 650_000, what: "Deposit kept on a cancelled project" },
];

/**
 * Zoho put every sale on one "Sales" account. The line's item name and
 * description say which kind of sale it was.
 */
export const zohoSalesCode = (line: Pick<ZohoInvoiceLine, "item" | "description">): string => {
  // The item name is the clearer signal; the description only when the
  // name says nothing (a BLAZE description talks about design and builds).
  const fromName = line.item ? salesCodeOf(line.item) : null;
  return fromName ?? salesCodeOf(line.description) ?? ZOHO_CODES.otherSales;
};

const salesCodeOf = (raw: string): string | null => {
  const text = norm(raw);
  if (/\b(develop|design|build|booking|gateway|milestone|deposit|settlement|blueprint|logo)/.test(text)) {
    return ZOHO_CODES.webSales;
  }
  if (/\b(maintenance|servicing|blaze|retainer|care plan|support)/.test(text)) return ZOHO_CODES.careSales;
  if (/\b(hosting|domain|renewal)/.test(text)) return ZOHO_CODES.hostingSales;
  if (/\b(website|web|app|software|seo)/.test(text)) return ZOHO_CODES.webSales;
  return null;
};

/* ---------------------------------------------------------------------------
 * Who an expense was for
 * ------------------------------------------------------------------------ */

const LEGAL_WORDS = new Set(["pty", "ltd", "cc", "the", "inc", "npc"]);
/** Words too common to point at one client on their own. */
const COMMON_WORDS = new Set([
  "agent", "app", "apps", "business", "charters", "digital", "fresh", "inspired", "media", "primary",
  "service", "services", "shuttle", "solutions", "studio", "studios", "tours", "travel",
]);

const squash = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, "");
const stem = (word: string) => (word.length > 4 && word.endsWith("s") ? word.slice(0, -1) : word);

/** The short forms of a name that would show up in a description. */
export const partyKeys = (name: string): string[] => {
  const words = norm(name)
    .split(" ")
    .filter((w) => w !== "" && !LEGAL_WORDS.has(w));
  if (words.length === 0) return [];
  const keys = new Set<string>([stem(squash(words.join("")))]);
  if (words.length > 1) keys.add(stem(squash(words.slice(0, 2).join(""))));
  if (words[0].length >= 5 && !COMMON_WORDS.has(words[0])) keys.add(stem(words[0]));
  return [...keys].filter((k) => k.length >= 4);
};

/**
 * The one client whose name shows up in `text`, or null when none or more
 * than one does.
 */
export const suggestParty = (text: string, parties: readonly string[]): string | null => {
  const haystack = squash(text);
  if (haystack === "") return null;
  const hits = new Set(parties.filter((p) => partyKeys(p).some((k) => haystack.includes(k))));
  return hits.size === 1 ? [...hits][0] : null;
};

/* ---------------------------------------------------------------------------
 * The plan
 * ------------------------------------------------------------------------ */

export type ImportKind = "invoice" | "payment" | "expense" | "loan" | "receipt";

export interface ImportLine {
  accountCode: string;
  debitCents: number;
  creditCents: number;
  memo: string;
  /** On a Trade debtors line: the invoice it bills or pays. */
  invoice?: string;
}

export interface ImportEntry {
  /** "zoho:invoice:INV-000010". The same record always gives the same ref. */
  ref: string;
  kind: ImportKind;
  date: string;
  narration: string;
  /** Who it was with: the customer, or the client the expense was for. */
  party: string | null;
  /** True when `party` was read from the description, not set in Zoho. */
  partyGuessed: boolean;
  project: string | null;
  lines: ImportLine[];
  totalCents: number;
  /** An invoice's due date. */
  dueOn?: string;
}

export interface ImportYearSummary {
  /** "2026-02-28" */
  yearEnd: string;
  entries: number;
  invoicedCents: number;
  receivedCents: number;
  spentCents: number;
  /** Shareholder money in, less what was paid back. */
  loansCents: number;
}

export interface ImportSkip {
  ref: string;
  date: string;
  reason: string;
}

export interface ZohoImportPlan {
  entries: ImportEntry[];
  skipped: ImportSkip[];
  years: ImportYearSummary[];
  /** Accounts the import will add to the chart before posting. */
  newAccounts: TemplateAccount[];
  /** Things for Liam to look at; none of them stop the import. */
  notes: string[];
  totals: { invoicedCents: number; receivedCents: number; spentCents: number; owedCents: number; loansCents: number };
  /** Journals already posted with a wrong date: posting reverses these, then books the corrected entry. */
  reversals: { ref: string; reason: string }[];
  /** What the bank would hold after these entries alone (compare with the bank statement). */
  endBankCents: number;
  /** The bank's lowest point if only these entries were in the books. */
  lowestBank: { date: string; cents: number } | null;
}

/** A Firestore-safe document id for a ref: the same ref always gives the same id. */
export const importJournalId = (ref: string) => ref.replace(/[^A-Za-z0-9-]+/g, "-").slice(0, 120);

const clip = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);
const narration = (text: string) => clip(text.replace(/\s+/g, " ").trim(), JOURNAL_LIMITS.narration);
const memo = (text: string) => clip(text.replace(/\s+/g, " ").trim(), JOURNAL_LIMITS.memo);

const invoiceEntry = (inv: ZohoInvoice): ImportEntry => {
  const credits = new Map<string, ImportLine>();
  inv.lines.forEach((line) => {
    const code = zohoSalesCode(line);
    const what = line.item || line.description;
    const existing = credits.get(code);
    if (existing) {
      existing.creditCents += line.amountCents;
      existing.memo = memo(`${existing.memo}; ${what}`);
    } else {
      credits.set(code, { accountCode: code, debitCents: 0, creditCents: line.amountCents, memo: memo(what) });
    }
  });
  const total = inv.lines.reduce((s, l) => s + l.amountCents, 0);
  return {
    ref: `zoho:invoice:${inv.number}`,
    kind: "invoice",
    date: inv.date,
    narration: narration(`Invoice ${inv.number} to ${inv.customer}`),
    party: inv.customer || null,
    partyGuessed: false,
    project: ZOHO_JOB_ASSIGNMENTS[inv.number] ?? (inv.project || null),
    lines: [
      { accountCode: ZOHO_CODES.debtors, debitCents: total, creditCents: 0, memo: memo(inv.customer), invoice: inv.number },
      ...credits.values(),
    ],
    totalCents: total,
    ...(inv.dueOn ? { dueOn: inv.dueOn } : {}),
  };
};

/** One Trade debtors credit per invoice the payment was applied to; anything Zoho left unused stays untagged. */
const paymentDebtorLines = (pay: ZohoPayment): ImportLine[] => {
  const applied = pay.invoices.filter((i) => i.amountCents > 0);
  const appliedCents = applied.reduce((s, i) => s + i.amountCents, 0);
  if (applied.length === 0 || appliedCents > pay.amountCents) {
    return [{ accountCode: ZOHO_CODES.debtors, debitCents: 0, creditCents: pay.amountCents, memo: memo(pay.customer) }];
  }
  const lines: ImportLine[] = applied.map((i) => ({
    accountCode: ZOHO_CODES.debtors,
    debitCents: 0,
    creditCents: i.amountCents,
    memo: memo(pay.customer),
    invoice: i.number,
  }));
  const unused = pay.amountCents - appliedCents;
  if (unused > 0) lines.push({ accountCode: ZOHO_CODES.debtors, debitCents: 0, creditCents: unused, memo: memo(`${pay.customer}: not used yet`) });
  return lines;
};

const paymentEntry = (pay: ZohoPayment): ImportEntry => {
  const paid = pay.invoices.map((i) => i.number).join(", ");
  return {
    // A payment whose date was corrected gets a new reference, so the old, wrong one can be reversed.
    ref: `zoho:payment:${pay.id}${pay.fixed ? ":fixed" : ""}`,
    kind: "payment",
    date: pay.date,
    narration: narration(`Payment ${pay.number} from ${pay.customer}${paid ? ` for ${paid}` : ""}`),
    party: pay.customer || null,
    partyGuessed: false,
    project: pay.invoices.map((i) => ZOHO_JOB_ASSIGNMENTS[i.number]).find(Boolean) ?? null,
    lines: [
      { accountCode: ZOHO_CODES.bank, debitCents: pay.amountCents, creditCents: 0, memo: memo(pay.reference || pay.customer) },
      ...paymentDebtorLines(pay),
    ],
    totalCents: pay.amountCents,
  };
};

const expenseEntry = (exp: ZohoExpense, parties: readonly string[]): ImportEntry => {
  const code = zohoExpenseCode(exp) ?? ZOHO_CODES.suspense;
  const cents = Math.abs(exp.amountCents);
  const refund = exp.amountCents < 0;
  const guess = exp.customer ? null : suggestParty(exp.description, parties);
  const what = exp.description || exp.account;
  const memoText = memo([exp.vendor, what].filter(Boolean).join(": "));
  const expenseLine = { accountCode: code, debitCents: refund ? 0 : cents, creditCents: refund ? cents : 0, memo: memoText };
  const bankLine = { accountCode: ZOHO_CODES.bank, debitCents: refund ? cents : 0, creditCents: refund ? 0 : cents, memo: memoText };
  return {
    ref: `zoho:expense:${exp.id}`,
    kind: "expense",
    date: exp.date,
    narration: narration(`${refund ? "Refund" : "Expense"} ${exp.entry}: ${what}`),
    party: exp.customer || guess,
    partyGuessed: guess !== null,
    project: exp.project || null,
    lines: [expenseLine, bankLine],
    totalCents: cents,
  };
};

const loanEntry = (loan: ZohoLoanLine): ImportEntry => {
  const code = ZOHO_LOAN_ACCOUNTS[norm(loan.shareholder)] ?? GENERAL_LOAN_CODE;
  const cents = loan.creditCents || loan.debitCents;
  const into = loan.creditCents > 0;
  return {
    ref: `zoho:loan:${loan.id}`,
    kind: "loan",
    date: loan.date,
    narration: narration(into ? `Loan from ${loan.shareholder}` : `Loan repaid to ${loan.shareholder}`),
    party: null,
    partyGuessed: false,
    project: null,
    lines: [
      { accountCode: ZOHO_CODES.bank, debitCents: into ? cents : 0, creditCents: into ? 0 : cents, memo: memo(`${loan.shareholder} shareholder loan`) },
      { accountCode: code, debitCents: into ? 0 : cents, creditCents: into ? cents : 0, memo: memo(loan.shareholder) },
    ],
    totalCents: cents,
  };
};

const receiptEntry = (r: (typeof ZOHO_SALES_RECEIPTS)[number]): ImportEntry => ({
  ref: `zoho:salesreceipt:${r.id}`,
  kind: "receipt",
  date: r.date,
  narration: narration(`Sales receipt from ${r.customer}: ${r.what}`),
  party: r.customer,
  partyGuessed: false,
  project: r.project,
  lines: [
    { accountCode: ZOHO_CODES.bank, debitCents: r.amountCents, creditCents: 0, memo: memo(r.what) },
    { accountCode: ZOHO_CODES.webSales, debitCents: 0, creditCents: r.amountCents, memo: memo(r.what) },
  ],
  totalCents: r.amountCents,
});

const bankMovement = (entry: ImportEntry) =>
  entry.lines
    .filter((l) => l.accountCode === ZOHO_CODES.bank)
    .reduce((s, l) => s + l.debitCents - l.creditCents, 0);

export interface ZohoImportOptions {
  /** The books' chart as it stands now. */
  accounts: readonly Pick<LedgerAccount, "code" | "archived">[];
  booksStart: string;
  lockedThrough: string | null;
  yearEndMonth: number;
  /** Refs already in the books, so a second run only adds what's new. */
  alreadyIn: ReadonlySet<string>;
  /** More client names to look for in expense descriptions (the CRM's clients). */
  otherParties?: readonly string[];
}

/** Small items under this are written off at once (SARS Interpretation Note 47). */
export const SMALL_ITEM_LIMIT_CENTS = 700_000;

export const buildZohoImport = (fullBundle: ZohoBundle, options: ZohoImportOptions): ZohoImportPlan => {
  const corrected: string[] = [];
  const reversals: ZohoImportPlan["reversals"] = [];
  const voided = fullBundle.invoices.filter((i) => i.number in ZOHO_VOIDED_INVOICES);
  const bundle: ZohoBundle = voided.length > 0 ? { ...fullBundle, invoices: fullBundle.invoices.filter((i) => !(i.number in ZOHO_VOIDED_INVOICES)) } : fullBundle;
  for (const inv of voided) {
    const ref = `zoho:invoice:${inv.number}`;
    if (options.alreadyIn.has(ref)) {
      reversals.push({ ref, reason: `${inv.number}: ${ZOHO_VOIDED_INVOICES[inv.number]}` });
      corrected.push(`${inv.number} (${inv.customer}, ${formatCents(inv.totalCents)}) is already in the books. Posting reverses it: ${ZOHO_VOIDED_INVOICES[inv.number]}`);
    } else {
      corrected.push(`${inv.number} (${inv.customer}, ${formatCents(inv.totalCents)}) is left out: ${ZOHO_VOIDED_INVOICES[inv.number]}`);
    }
  }
  const payments = bundle.payments.map((pay) => {
    const fix = ZOHO_DATE_CORRECTIONS[pay.id];
    if (!fix || fix.from !== pay.date) return pay;
    corrected.push(`Payment ${pay.number} (${pay.customer}) is dated ${fix.from} in Zoho, but the bank statement shows it on ${fix.to}. It is booked on ${fix.to}.`);
    const original = `zoho:payment:${pay.id}`;
    if (options.alreadyIn.has(original)) {
      reversals.push({ ref: original, reason: `Zoho dated payment ${pay.number} ${fix.from}; the bank shows ${fix.to}. Booked again on the right date.` });
      corrected.push(`Payment ${pay.number} is already in with the wrong date. Posting reverses that journal and books it again on ${fix.to}.`);
    }
    return { ...pay, date: fix.to, fixed: true };
  });
  for (const number of ZOHO_ASSUMED_PAID) {
    const invoice = bundle.invoices.find((i) => i.number === number);
    if (!invoice) continue;
    const paid = payments.reduce((sum, p) => sum + p.invoices.filter((i) => i.number === number).reduce((n, i) => n + i.amountCents, 0), 0);
    const owing = invoice.totalCents - paid;
    if (owing <= 0) continue;
    payments.push({
      id: `assumed-${number}`,
      number: "(not in Zoho)",
      date: invoice.date,
      customer: invoice.customer,
      amountCents: owing,
      reference: "Paid, per Liam; not on the FNB statements",
      invoices: [{ number, amountCents: owing }],
    });
    corrected.push(`${number} (${invoice.customer}) is booked as paid on ${invoice.date}, because you said it was. Zoho has no payment and it isn't on the FNB statements, so the books will be ${owing / 100} higher than FNB until you give the real account and date.`);
  }
  const customers = [
    ...new Set([...bundle.invoices.map((i) => i.customer), ...payments.map((p) => p.customer), ...bundle.contacts, ...(options.otherParties ?? [])]),
  ].filter(Boolean);
  const all: ImportEntry[] = [
    ...bundle.invoices.map(invoiceEntry),
    ...payments.map(paymentEntry),
    ...bundle.expenses.map((e) => expenseEntry(e, customers)),
    ...bundle.loans.map(loanEntry),
    ...(bundle.loans.length > 0 ? ZOHO_SALES_RECEIPTS.map(receiptEntry) : []),
  ].sort((a, b) => a.date.localeCompare(b.date) || a.ref.localeCompare(b.ref));

  const skipped: ImportSkip[] = [];
  const entries = all.filter((entry) => {
    let reason = "";
    if (options.alreadyIn.has(entry.ref)) reason = "Already in the books.";
    else if (entry.totalCents === 0) reason = "Nothing to post: the amount is zero.";
    else if (entry.date < options.booksStart) reason = `Before the books start (${options.booksStart}).`;
    else if (options.lockedThrough && entry.date <= options.lockedThrough) reason = `In a locked period (up to ${options.lockedThrough}).`;
    if (reason) skipped.push({ ref: entry.ref, date: entry.date, reason });
    return reason === "";
  });

  const have = new Map(options.accounts.map((a) => [a.code, a]));
  const used = new Set(entries.flatMap((e) => e.lines.map((l) => l.accountCode)));
  const newAccounts = ZOHO_EXTRA_ACCOUNTS.filter((a) => used.has(a.code) && !have.has(a.code));
  const notes: string[] = [...corrected];
  for (const code of used) {
    const account = have.get(code);
    const adding = newAccounts.some((a) => a.code === code);
    if (!account && !adding) notes.push(`Account ${code} isn't in the chart. Add it back before importing.`);
    if (account?.archived) notes.push(`Account ${code} is archived. Restore it before importing.`);
  }

  const years = new Map<string, ImportYearSummary>();
  let invoiced = 0;
  let received = 0;
  let spent = 0;
  let loans = 0;
  for (const entry of entries) {
    const yearEnd = financialYearEnd(entry.date, options.yearEndMonth);
    const year = years.get(yearEnd) ?? { yearEnd, entries: 0, invoicedCents: 0, receivedCents: 0, spentCents: 0, loansCents: 0 };
    year.entries += 1;
    const signed = entry.kind === "expense" ? -bankMovement(entry) : entry.totalCents;
    const loanSigned = entry.kind === "loan" ? bankMovement(entry) : 0;
    if (entry.kind === "invoice") year.invoicedCents += signed;
    if (entry.kind === "payment" || entry.kind === "receipt") year.receivedCents += signed;
    if (entry.kind === "expense") year.spentCents += signed;
    year.loansCents += loanSigned;
    years.set(yearEnd, year);
    if (entry.kind === "invoice") invoiced += signed;
    if (entry.kind === "payment" || entry.kind === "receipt") received += signed;
    if (entry.kind === "expense") spent += signed;
    loans += loanSigned;
  }

  // Review notes: the records Zoho holds that need Liam's eye.
  const invoiceNumbers = new Set(bundle.invoices.map((i) => i.number));
  for (const inv of bundle.invoices) {
    const lineTotal = inv.lines.reduce((s, l) => s + l.amountCents, 0);
    if (lineTotal !== inv.totalCents) {
      notes.push(`${inv.number}: the lines add up to ${formatCents(lineTotal)} but Zoho's total is ${formatCents(inv.totalCents)}. The lines were used.`);
    }
    if (inv.lines.some((l) => zohoSalesCode(l) === ZOHO_CODES.otherSales)) {
      notes.push(`${inv.number} (${inv.customer}) isn't web work, so it goes to Other sales: ${inv.lines.map((l) => l.item || l.description).join(", ")}.`);
    }
  }
  const owedByInvoice = new Map(bundle.invoices.map((i) => [i.number, i.totalCents]));
  for (const pay of payments) {
    for (const applied of pay.invoices) {
      if (!invoiceNumbers.has(applied.number)) {
        notes.push(`Payment ${pay.number} (${pay.customer}) paid ${applied.number}, which isn't in the invoices file.`);
      } else {
        owedByInvoice.set(applied.number, (owedByInvoice.get(applied.number) ?? 0) - applied.amountCents);
      }
      const invoice = bundle.invoices.find((i) => i.number === applied.number);
      if (invoice && pay.date < invoice.date) {
        notes.push(`Payment ${pay.number} (${pay.customer}, ${pay.date}) is dated before ${invoice.number} (${invoice.date}). The money came in first, so the books show it owed back to them until the invoice date.`);
      }
    }
    const appliedTotal = pay.invoices.reduce((s, i) => s + i.amountCents, 0);
    if (appliedTotal < pay.amountCents) {
      notes.push(`Payment ${pay.number} (${pay.customer}) left ${formatCents(pay.amountCents - appliedTotal)} unused. It stays as their credit.`);
    }
  }
  for (const [number, cents] of owedByInvoice) {
    if (cents > 0) {
      const inv = bundle.invoices.find((i) => i.number === number);
      notes.push(`${number} (${inv?.customer ?? ""}, ${inv?.date ?? ""}) still has ${formatCents(cents)} unpaid. Is that still owed?`);
    }
  }
  const numbered = [...invoiceNumbers]
    .map((n) => /^(.*?)(\d+)$/.exec(n))
    .filter((m): m is RegExpExecArray => m !== null);
  if (numbered.length > 1) {
    const prefix = numbered[0][1];
    const width = numbered[0][2].length;
    const nums = new Set(numbered.map((m) => Number(m[2])));
    const max = Math.max(...nums);
    for (let n = Math.min(...nums); n <= max; n += 1) {
      if (!nums.has(n) && !(prefix === "INV-" && ZOHO_KNOWN_GAPS.includes(n)) && !((`${prefix}${String(n).padStart(width, "0")}`) in ZOHO_VOIDED_INVOICES)) notes.push(`${prefix}${String(n).padStart(width, "0")} isn't in the export. Was it voided or deleted?`);
    }
  }
  const paidCounts = new Map<string, number>();
  for (const exp of bundle.expenses) paidCounts.set(norm(exp.paidThrough), (paidCounts.get(norm(exp.paidThrough)) ?? 0) + 1);
  const usualBank = [...paidCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "";
  for (const exp of bundle.expenses) {
    if (!(norm(exp.account) in ZOHO_EXPENSE_ACCOUNTS)) {
      notes.push(`Expense ${exp.entry} (${exp.date}) used the Zoho account "${exp.account}", which has no match, so it goes to Suspense.`);
    }
    if (exp.paidThrough && norm(exp.paidThrough) !== usualBank) {
      notes.push(`Expense ${exp.entry} (${exp.date}) was paid through "${exp.paidThrough}", not the business bank. It was still booked from the bank.`);
    }
    const code = zohoExpenseCode(exp);
    if ((code === "6260" || code === "6410") && exp.amountCents >= SMALL_ITEM_LIMIT_CENTS) {
      notes.push(`Expense ${exp.entry} (${exp.date}, ${formatCents(exp.amountCents)}) is R7,000 or more, so it is probably an asset to depreciate, not an expense.`);
    }
  }
  const unlinked = entries.filter((e) => e.kind === "expense" && !e.party).length;
  if (unlinked > 0) {
    notes.push(`${unlinked} expenses don't name a client. That's normal for software, bank fees and the like.`);
  }

  // The bank's running balance, and what customers owe, over everything that
  // will be in the books once this is posted: what is already in plus what is new.
  const counted = all.filter((e) => entries.includes(e) || options.alreadyIn.has(e.ref));
  let owed = 0;
  for (const entry of counted) {
    if (entry.kind === "invoice") owed += entry.totalCents;
    if (entry.kind === "payment") owed -= entry.totalCents;
  }
  let bank = 0;
  let lowestBank: ZohoImportPlan["lowestBank"] = null;
  for (const entry of counted) {
    bank += bankMovement(entry);
    if (!lowestBank || bank < lowestBank.cents) lowestBank = { date: entry.date, cents: bank };
  }
  if (lowestBank && lowestBank.cents < 0) {
    notes.push(
      bundle.loans.length > 0
        ? `On these entries alone the bank dips to ${formatCents(-lowestBank.cents)} overdrawn on ${lowestBank.date}, before the next shareholder loan lands.`
        : `On these entries alone the bank would be ${formatCents(-lowestBank.cents)} overdrawn on ${lowestBank.date}. The money that covered it (your loans to the company or share capital) isn't in these files. Add each shareholder's loan export (Zoho's Account Transactions for the loan account).`,
    );
  }
  for (const loan of bundle.loans) {
    if (!(norm(loan.shareholder) in ZOHO_LOAN_ACCOUNTS)) {
      notes.push(`${loan.shareholder} has no loan account of their own yet, so ${loan.date} goes to the general director's loan.`);
      break;
    }
  }

  return {
    entries,
    skipped,
    years: [...years.values()].sort((a, b) => a.yearEnd.localeCompare(b.yearEnd)),
    newAccounts,
    notes,
    totals: { invoicedCents: invoiced, receivedCents: received, spentCents: spent, owedCents: owed, loansCents: loans },
    reversals,
    endBankCents: bank,
    lowestBank,
  };
};
